import React, { Suspense, lazy, useContext, useEffect, useState } from "react";
import { CreatorContext } from "../context/CreatorContext";
import { WebTemplateContext } from "../context/WebTemplateContext";
import useDynamicHead from "../hooks/useDynamicHead";
import { loadFonts, TEMPLATE_EXTRA_FONTS } from "../utils/loadFont";
import { getTemplateOverride } from "../utils/templateOverride";
import DemoNoticeModal from "./DemoNoticeModal";
import AnnouncementBar from "./AnnouncementBar";

const TEMPLATES_WITH_OWN_ANNOUNCEMENT = new Set([
	"shop1",
	"shop2",
	"shop3",
	"shop4",
	"shop5",
	"shop6",
	"drugstore",
	"flora",
	"riquino",
	"parcelona",
	"rovica",
	"gaps",
]);

const templates = {
	restaurant1: lazy(() => import("../templates/restaurant1")),
	restaurant2: lazy(() => import("../templates/restaurant2")),
	restaurant3: lazy(() => import("../templates/restaurant3")),
	restaurant4: lazy(() => import("../templates/restaurant4")),
	restaurant5: lazy(() => import("../templates/restaurant5")),
	restaurant6: lazy(() => import("../templates/restaurant6")),
	restaurant7: lazy(() => import("../templates/restaurant7")),
	restaurant8: lazy(() => import("../templates/restaurant8")),
	restaurant9: lazy(() => import("../templates/restaurant9/restaurant9.js")),
	restaurant10: lazy(() => import("../templates/restaurant10/restaurant10.js")),
	restaurant11: lazy(() => import("../templates/restaurant11/restaurant11.js")),
	restaurant12: lazy(() => import("../templates/restaurant12/index.jsx")),
	restaurant13: lazy(() => import("../templates/restaurant13/index.js")),
	restaurant14: lazy(() => import("../templates/restaurant14/index.js")),
	restaurant15: lazy(() => import("../templates/restaurant15/index.js")),
	restaurant16: lazy(() => import("../templates/restaurant16/index.js")),
	restaurant17: lazy(() => import("../templates/restaurant17/index.js")),
	restaurant18: lazy(() => import("../templates/restaurant18/index.js")),
	restaurant19: lazy(() => import("../templates/restaurant19/index.js")),
	restaurant20: lazy(() => import("../templates/restaurant20/index.js")),
	burgerbar: lazy(() => import("../templates/burgerbar/index.js")),
	grillhouse: lazy(() => import("../templates/grillhouse/index.js")),
	brunch: lazy(() => import("../templates/brunch/index.js")),
	barberiamarco: lazy(() => import("../templates/barberiamarco/index.js")),
	barberiamarco2: lazy(() => import("../templates/barberiamarco2/index.js")),
	barber1: lazy(() => import("../templates/barber1/index.js")),
	barber2: lazy(() => import("../templates/barber2/index.js")),
	barber3: lazy(() => import("../templates/barber3/index.js")),
	barber4: lazy(() => import("../templates/barber4/index.js")),
	barber5: lazy(() => import("../templates/barber5/index.js")),
	barber6: lazy(() => import("../templates/barber6/index.js")),
	barber7: lazy(() => import("../templates/barber7/index.js")),
	barber8: lazy(() => import("../templates/barber8/index.js")),
	hairdresser1: lazy(() => import("../templates/hairdresser1/index.js")),
	hairdresser2: lazy(() => import("../templates/hairdresser2/index.js")),
	muse: lazy(() => import("../templates/muse/index.js")),
	vianca: lazy(() => import("../templates/vianca/index.js")),
	essenza: lazy(() => import("../templates/essenza/index.js")),
	riquino: lazy(() => import("../templates/riquino/index.js")),
	flora: lazy(() => import("../templates/flora/index.js")),
	rovica: lazy(() => import("../templates/rovica/index.js")),
	parcelona: lazy(() => import("../templates/parcelona/index.js")),
	balmar: lazy(() => import("../templates/balmar/index.js")),
	shop1: lazy(() => import("../templates/shop1")),
	shop2: lazy(() => import("../templates/shop2")),
	shop3: lazy(() => import("../templates/shop3")),
	shop4: lazy(() => import("../templates/shop4")),
	shop5: lazy(() => import("../templates/shop5")),
	shop6: lazy(() => import("../templates/shop6")),
	drugstore: lazy(() => import("../templates/drugstore")),
	trainer1: lazy(() => import("../templates/trainer1")),
	gaps: lazy(() => import("../templates/gaps")),
};

function LoadingSkeleton({ color }) {
	return (
		<div className="flex flex-col items-center justify-center min-h-[60vh]">
			<div
				className="w-10 h-10 border-4 border-gray-300 rounded-full animate-spin"
				style={{ borderTopColor: color || "#000000" }}
			></div>
		</div>
	);
}

const ENABLE_DEMO_NOTICE = false;

export default function Main() {
	const { creator } = useContext(CreatorContext);
	const { webTemplate: templateData, isDemoContent } =
		useContext(WebTemplateContext);
	const [selectedTemplate, setSelectedTemplate] = useState(
		creator?.webTemplate,
	);
	const [showDemoModal, setShowDemoModal] = useState(false);
	const [hasCheckedDismissed, setHasCheckedDismissed] = useState(false);

	const canonical =
		typeof window !== "undefined"
			? window.location.origin + window.location.pathname.replace(/\/$/, "")
			: undefined;

	useDynamicHead({
		title: creator?.app?.name,
		favicon: creator?.faviconUrl || templateData?.logo,
		description: templateData?.description || creator?.app?.description,
		themeColor: templateData?.backgroundColor,
		ogImage:
			templateData?.heroImage || templateData?.logo || creator?.faviconUrl,
		lang: templateData?.language,
		canonical,
	});

	useEffect(() => {
		const templateOverride = getTemplateOverride();
		if (templateOverride) {
			const isNumeric = /^\d+$/.test(templateOverride);
			setSelectedTemplate(
				isNumeric ? `restaurant${templateOverride}` : templateOverride,
			);
		} else {
			setSelectedTemplate(creator?.webTemplate);
		}
	}, [creator]);

	useEffect(() => {
		if (!ENABLE_DEMO_NOTICE) {
			setHasCheckedDismissed(true);
			return;
		}

		if (isDemoContent === null || hasCheckedDismissed) return;

		const dismissed = localStorage.getItem("applaby_demo_notice_dismissed");

		if (isDemoContent && !dismissed) {
			const timer = setTimeout(() => {
				setShowDemoModal(true);
				setHasCheckedDismissed(true);
			}, 500);
			return () => clearTimeout(timer);
		} else {
			setHasCheckedDismissed(true);
		}
	}, [isDemoContent, hasCheckedDismissed]);

	const handleCloseDemoModal = () => {
		try {
			localStorage.setItem("applaby_demo_notice_dismissed", "true");
		} catch (e) {
			console.warn("No se pudo guardar en localStorage:", e);
		}
		setShowDemoModal(false);
	};

	const getTemplateKey = () => {
		const appType = creator?.app?.appType?.toLowerCase();
		const appSubtype = creator?.app?.appSubtype?.toLowerCase();
		const isEcommerce = appType === "ecommerce" || appType === "woocommerce";
		const isRestaurant = appSubtype === "restaurant";
		const isTrainer = appSubtype === "trainer";
		const isBarber = appSubtype === "barber" || appSubtype === "salon";

		// GAPS es una template restringida: solo si el negocio tiene el módulo gapsContent activo.
		const hasGapsModule = creator?.app?.modules?.gapsContent === true;
		if (selectedTemplate === "gaps") {
			return hasGapsModule ? "gaps" : "shop1";
		}
		if (isEcommerce && !isRestaurant) {
			const allowed = ["shop1", "shop2", "shop3", "shop4", "shop5", "shop6", "drugstore"];
			if (hasGapsModule) allowed.push("gaps");
			return allowed.includes(selectedTemplate) ? selectedTemplate : "shop1";
		}
		if (isTrainer) {
			return "trainer1";
		}
		if (isBarber) {
			const allowed = ["barber1", "barber2", "barber3", "barber4", "barber5", "barber6", "barber7", "barber8", "hairdresser1", "hairdresser2", "muse", "barberiamarco", "barberiamarco2", "vianca", "essenza"];
			return allowed.includes(selectedTemplate) ? selectedTemplate : "barber1";
		}
		return templates[selectedTemplate] ? selectedTemplate : "restaurant1";
	};

	const templateKey = selectedTemplate ? getTemplateKey() : null;

	useEffect(() => {
		if (!templateKey) return;
		const extraFonts = TEMPLATE_EXTRA_FONTS[templateKey];
		if (extraFonts?.length) {
			loadFonts(...extraFonts);
		}
	}, [templateKey]);

	if (!selectedTemplate) {
		return <LoadingSkeleton color={templateData?.mainColor} />;
	}

	const TemplateComponent = templates[templateKey];

	return (
		<>
			{!TEMPLATES_WITH_OWN_ANNOUNCEMENT.has(templateKey) && (
				<AnnouncementBar />
			)}
			<Suspense fallback={<LoadingSkeleton color={templateData?.mainColor} />}>
				<TemplateComponent />
			</Suspense>
			<DemoNoticeModal
				visible={showDemoModal}
				onClose={handleCloseDemoModal}
				webTemplate={templateData}
			/>
		</>
	);
}
